/**
 * HTTP client + write-through queue for the terminal session-sync API.
 *
 * Reliability model — designed so user data is never lost:
 *
 *  - Writes are coalesced per document (latest payload wins) and debounced;
 *    the local store (localStorage) is always written first by the caller,
 *    so a crashed upload only delays sync, never loses the canonical copy.
 *  - Every PUT carries `If-Match` with the last rev we saw. On 412 the
 *    client fetches the remote doc, asks the app to merge
 *    (`resolveConflict`), reports the merged doc back (`onMerged`, so the
 *    app can persist it locally), and retries with the fresh rev.
 *  - Failed uploads stay pending and are retried on the retry interval and
 *    on the next `flush()`. `flush()` is what sign-out awaits: it returns
 *    `true` only when nothing is left pending.
 */

import { syncDocKey } from './types';
import type {
  SyncDocRef,
  SyncEnvelope,
  SyncFolderLike,
  SyncIndexDoc,
  SyncMessageLike,
  SyncMessagesDoc,
  SyncSessionLike,
  SyncSnapshot,
} from './types';

export interface TerminalSyncStatus {
  readonly pending: number;
  /**
   * The most recent failure, as `<op>_<status>` (`put_413`, `put_507`) or a
   * transport code (`network_error`, `no_auth`). Cleared by the next request
   * that succeeds — ANY request, so a dropped session's `put_413` is gone the
   * moment the index PUTs. `droppedSessionIds` is the durable record.
   */
  readonly lastError: string | null;
  /**
   * Session documents the client has given up on: a PUT answered with a 4xx
   * the same payload cannot recover from (413 oversized, 400 malformed). The
   * transcript is still on this device and still in the index other devices
   * see, so they show the chat with an older transcript — the one failure
   * mode a user actually notices. A session leaves this set when a later PUT
   * of it succeeds (the payload changed: trimmed, or a smaller upload).
   */
  readonly droppedSessionIds: readonly string[];
}

export interface TerminalSyncClientConfig {
  /** e.g. `https://dave.example.com` — no trailing slash, no path. */
  readonly baseUrl: string;
  /**
   * Auth headers for every request (`Authorization: Bearer …` and, in token
   * mode, `X-Sync-User`). Return null when no credential is currently
   * available — the client treats that as transient and keeps retrying.
   */
  readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
  /**
   * Merge a locally queued doc with the remote copy after a rev conflict.
   * Must never throw; must not lose data from either side.
   */
  readonly resolveConflict: (
    ref: SyncDocRef,
    localDoc: unknown,
    remoteDoc: unknown,
  ) => unknown;
  /** Called with the merged doc after conflict resolution so the app can persist it locally. */
  readonly onMerged?: (ref: SyncDocRef, mergedDoc: unknown) => void;
  /** Status callback (pending count / last error) for UI indicators. */
  readonly onStatusChange?: (status: TerminalSyncStatus) => void;
  readonly fetchFn?: typeof fetch;
  /** Debounce for queued writes. Default 2000ms. */
  readonly debounceMs?: number;
  /** Background retry interval while uploads are pending. Default 30000ms. */
  readonly retryIntervalMs?: number;
  /** Per-request timeout. Default 10000ms. */
  readonly requestTimeoutMs?: number;
}

type PendingOp =
  | { readonly ref: SyncDocRef; readonly op: 'put'; readonly doc: unknown }
  | { readonly ref: SyncDocRef; readonly op: 'delete' };

/**
 * Per-request outcome the drain loop interprets:
 *  - 'synced':  reached the gateway with 2xx. Remove from pending and
 *               count as success.
 *  - 'drop':    permanent failure (a 4xx the same payload can't recover
 *               from — 400/404/413/422 etc). Remove from pending so we
 *               don't retry forever, BUT mark flush() as failed so the
 *               desktop sign-out wipe takes the retention path instead
 *               of treating the data as synced.
 *  - 'pending': transient failure (network, auth, 429, 412 retry, 5xx).
 *               Keep in pending; the next flush / retry timer will try
 *               again. flush() returns false.
 */
type PutOutcome = 'synced' | 'drop' | 'pending';

const DEFAULT_DEBOUNCE_MS = 2_000;
const DEFAULT_RETRY_INTERVAL_MS = 30_000;
const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

function refPath(ref: SyncDocRef): string {
  if (ref.kind === 'index') return '/sync/v1/index';
  if (ref.kind === 'ui-state') return '/sync/v1/ui-state';
  return `/sync/v1/sessions/${encodeURIComponent(ref.sessionId)}`;
}

export class TerminalSyncClient<
  M extends SyncMessageLike,
  S extends SyncSessionLike,
  F extends SyncFolderLike,
> {
  private readonly config: TerminalSyncClientConfig;

  /** Last rev seen per doc key — the If-Match token for the next PUT. */
  private readonly revs = new Map<string, number>();

  private readonly pending = new Map<string, PendingOp>();

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  private flushPromise: Promise<boolean> | null = null;

  private lastError: string | null = null;

  /** Session ids whose last PUT was a permanent drop — see `TerminalSyncStatus`. */
  private readonly droppedSessions = new Set<string>();

  private disposed = false;

  public constructor(config: TerminalSyncClientConfig) {
    this.config = config;
  }

  // ─── Reads ───────────────────────────────────────────────────────────────

  /**
   * One-shot hydration read. Returns null when the gateway is unreachable
   * or auth is unavailable — callers treat that as "sync offline" and
   * continue local-only.
   */
  public async fetchSnapshot(): Promise<SyncSnapshot<M, S, F> | null> {
    const resp = await this.request('GET', '/sync/v1/snapshot');
    if (resp == null || !resp.ok) return null;
    const snapshot = (await resp.json()) as SyncSnapshot<M, S, F>;
    if (snapshot.index != null) this.revs.set('index', snapshot.index.rev);
    if (snapshot.uiState != null)
      this.revs.set('ui-state', snapshot.uiState.rev);
    for (const [id, envelope] of Object.entries(snapshot.sessions)) {
      this.revs.set(`session:${id}`, envelope.rev);
    }
    // Record revs for sessions not included in the snapshot body too, so a
    // later PUT to them doesn't start a pointless 412 round-trip.
    for (const item of snapshot.sessionList) {
      if (!this.revs.has(`session:${item.id}`)) {
        this.revs.set(`session:${item.id}`, item.rev);
      }
    }
    return snapshot;
  }

  /** Lazy-load one transcript that wasn't included in the snapshot. */
  public async fetchSession(
    sessionId: string,
  ): Promise<SyncEnvelope<SyncMessagesDoc<M>> | null> {
    const ref: SyncDocRef = { kind: 'session', sessionId };
    const resp = await this.request('GET', refPath(ref));
    if (resp == null || !resp.ok) return null;
    const envelope = (await resp.json()) as SyncEnvelope<SyncMessagesDoc<M>>;
    this.revs.set(syncDocKey(ref), envelope.rev);
    return envelope;
  }

  // ─── Queued writes ───────────────────────────────────────────────────────

  public queueIndex(doc: SyncIndexDoc<S, F>): void {
    this.enqueue({ ref: { kind: 'index' }, op: 'put', doc });
  }

  public queueUiState(doc: Record<string, unknown>): void {
    this.enqueue({ ref: { kind: 'ui-state' }, op: 'put', doc });
  }

  public queueSession(sessionId: string, doc: SyncMessagesDoc<M>): void {
    this.enqueue({ ref: { kind: 'session', sessionId }, op: 'put', doc });
  }

  /**
   * Queue a transcript deletion. The caller must ALSO queue an index doc
   * containing the matching tombstone — the tombstone, not this delete, is
   * what prevents other devices from resurrecting the session.
   */
  public queueDeleteSession(sessionId: string): void {
    this.enqueue({ ref: { kind: 'session', sessionId }, op: 'delete' });
  }

  public pendingCount(): number {
    return this.pending.size;
  }

  public getLastError(): string | null {
    return this.lastError;
  }

  /**
   * Push everything pending now. Returns true ONLY when every doc reached
   * the gateway with a 2xx — the signal sign-out waits for before wiping
   * local state. A permanently-dropped payload (e.g. a 413 oversized
   * doc) is removed from the retry queue (same payload can't recover)
   * but still makes flush() return false, so the desktop sign-out path
   * takes the bounded-retention branch rather than wiping data that
   * never actually synced.
   *
   * Serialized: at most one drain runs at a time. Concurrent callers (the
   * debounce/retry timer racing a sign-out flush, or two sign-out paths)
   * queue behind the in-flight one and start a fresh drain only if work
   * was added after it began.
   */
  public async flush(): Promise<boolean> {
    if (this.disposed) return this.pending.size === 0;
    if (this.debounceTimer != null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    // Wait out any drain already in progress. Using a while loop (not a
    // single `await`) covers the case where another caller wins the race
    // and starts a fresh drain while we were awaiting the first.
    while (this.flushPromise != null) {
      await this.flushPromise;
    }
    // dispose() may have been called while we awaited; bail before
    // issuing any requests. TS narrowed `this.disposed` to `false` from
    // the early-return at the top — that narrowing isn't sound across
    // an `await`, so check via the helper which doesn't get narrowed.
    if (this.isDisposed() || this.pending.size === 0) {
      return this.pending.size === 0;
    }
    this.flushPromise = this.drain();
    try {
      return await this.flushPromise;
    } finally {
      this.flushPromise = null;
    }
  }

  /**
   * Internal — read `disposed` via a method so TS doesn't narrow it from
   * the early-return at the top of `flush()`. The narrowing isn't sound
   * across an `await`: `dispose()` can be called while we're suspended.
   */
  private isDisposed(): boolean {
    return this.disposed;
  }

  public dispose(): void {
    this.disposed = true;
    if (this.debounceTimer != null) clearTimeout(this.debounceTimer);
    if (this.retryTimer != null) clearTimeout(this.retryTimer);
    this.debounceTimer = null;
    this.retryTimer = null;
  }

  // ─── Internals ───────────────────────────────────────────────────────────

  private enqueue(op: PendingOp): void {
    if (this.disposed) return;
    // Latest write per doc wins; a delete supersedes pending puts and
    // vice versa (the app's local store already reflects the final intent).
    this.pending.set(syncDocKey(op.ref), op);
    this.notifyStatus();
    if (this.debounceTimer != null) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.flushSafely();
    }, this.config.debounceMs ?? DEFAULT_DEBOUNCE_MS);
  }

  /** Fire-and-forget flush for timers; `flush()` itself never rejects. */
  private flushSafely(): void {
    this.flush().catch(() => {
      // Unreachable — drain() converts every failure into `false` and
      // records it in lastError.
    });
  }

  private async drain(): Promise<boolean> {
    let allOk = true;
    for (const key of this.pending.keys()) {
      const op = this.pending.get(key);
      if (op == null) continue;
      let outcome: PutOutcome;
      try {
        if (op.op === 'delete') {
          const ok = await this.deleteNow(op.ref);
          outcome = ok ? 'synced' : 'pending';
        } else {
          outcome = await this.putNow(op.ref, op.doc);
        }
      } catch (_error) {
        // Defensive: unexpected failures (e.g. a malformed response body)
        // must keep the payload pending rather than reject the flush.
        this.lastError = 'sync_error';
        outcome = 'pending';
      }
      if (outcome === 'synced') {
        // Only clear if no newer payload was queued for this doc while the
        // request was in flight.
        if (this.pending.get(key) === op) this.pending.delete(key);
        if (op.ref.kind === 'session') {
          this.droppedSessions.delete(op.ref.sessionId);
        }
      } else if (outcome === 'drop') {
        // Permanent failure: remove from queue (same payload can't
        // recover), but flush() must still report not-OK so callers
        // know the data didn't reach the gateway — and remember the
        // session, because `lastError` is cleared by the next success of
        // ANY doc and this is the failure a user can actually see.
        if (this.pending.get(key) === op) this.pending.delete(key);
        if (op.ref.kind === 'session') {
          this.droppedSessions.add(op.ref.sessionId);
        }
        allOk = false;
      } else {
        // 'pending' — transient failure; leave in queue, retry later.
        allOk = false;
      }
    }
    this.notifyStatus();
    if (this.pending.size > 0) this.scheduleRetry();
    return allOk && this.pending.size === 0;
  }

  private scheduleRetry(): void {
    if (this.disposed || this.retryTimer != null) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.flushSafely();
    }, this.config.retryIntervalMs ?? DEFAULT_RETRY_INTERVAL_MS);
  }

  private async putNow(ref: SyncDocRef, doc: unknown): Promise<PutOutcome> {
    const key = syncDocKey(ref);
    const rev = this.revs.get(key) ?? 0;
    const resp = await this.request('PUT', refPath(ref), doc, rev);
    if (resp == null) return 'pending';
    if (resp.ok) {
      const body = (await resp.json()) as { rev: number };
      this.revs.set(key, body.rev);
      this.lastError = null;
      return 'synced';
    }
    if (resp.status === 412) {
      const ok = await this.resolveAndRetry(ref, doc);
      return ok ? 'synced' : 'pending';
    }
    this.lastError = `put_${resp.status}`;
    // 401/403 are transient (token rotation, credential not yet synced)
    // and 429/5xx recover on their own — keep those pending.
    if (
      resp.status === 401 ||
      resp.status === 403 ||
      resp.status === 429 ||
      resp.status >= 500
    ) {
      return 'pending';
    }
    // Other 4xx (413 oversized payload, 400 malformed, 422 quota) will
    // never succeed with the same payload, so drop them from the retry
    // queue to avoid an eternal loop. flush() still reports this as a
    // failure so the desktop sign-out wipe takes the retention path
    // instead of treating the data as synced.
    return 'drop';
  }

  private async resolveAndRetry(
    ref: SyncDocRef,
    localDoc: unknown,
  ): Promise<boolean> {
    const key = syncDocKey(ref);
    // The 412 carried the current rev in ETag; fetch the remote doc to merge.
    const remoteResp = await this.request('GET', refPath(ref));
    if (remoteResp == null) return false;
    let remoteDoc: unknown = null;
    let remoteRev = 0;
    if (remoteResp.ok) {
      const envelope = (await remoteResp.json()) as SyncEnvelope<unknown>;
      remoteDoc = envelope.doc;
      remoteRev = envelope.rev;
    } else if (remoteResp.status === 404) {
      // Doc vanished between the 412 and the GET (deleted elsewhere);
      // re-upload ours against rev 0.
      remoteRev = 0;
    } else {
      this.lastError = `conflict_get_${remoteResp.status}`;
      return false;
    }
    const merged =
      remoteDoc == null
        ? localDoc
        : this.config.resolveConflict(ref, localDoc, remoteDoc);
    this.config.onMerged?.(ref, merged);
    this.revs.set(key, remoteRev);
    const retry = await this.request('PUT', refPath(ref), merged, remoteRev);
    if (retry == null) return false;
    if (retry.ok) {
      const body = (await retry.json()) as { rev: number };
      this.revs.set(key, body.rev);
      this.lastError = null;
      return true;
    }
    // Lost another race — leave pending; the retry timer will try again.
    this.lastError = `put_retry_${retry.status}`;
    return false;
  }

  private async deleteNow(ref: SyncDocRef): Promise<boolean> {
    const resp = await this.request('DELETE', refPath(ref));
    if (resp == null) return false;
    if (resp.ok || resp.status === 404) {
      this.revs.delete(syncDocKey(ref));
      this.lastError = null;
      return true;
    }
    this.lastError = `delete_${resp.status}`;
    return false;
  }

  /** Returns null on network failure or missing auth (both transient). */
  private async request(
    method: 'GET' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
    ifMatchRev?: number,
  ): Promise<Response | null> {
    const auth = this.config.getAuthHeaders();
    if (auth == null) {
      this.lastError = 'no_auth';
      return null;
    }
    const fetchFn = this.config.fetchFn ?? fetch;
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      this.config.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS,
    );
    try {
      return await fetchFn(`${this.config.baseUrl}${path}`, {
        method,
        headers: {
          ...auth,
          ...(body !== undefined && { 'Content-Type': 'application/json' }),
          ...(ifMatchRev !== undefined && { 'If-Match': `"${ifMatchRev}"` }),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (_error) {
      this.lastError = 'network_error';
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  /** Current sync health; the same shape `onStatusChange` reports. */
  public getStatus(): TerminalSyncStatus {
    return {
      pending: this.pending.size,
      lastError: this.lastError,
      droppedSessionIds: [...this.droppedSessions],
    };
  }

  private notifyStatus(): void {
    this.config.onStatusChange?.(this.getStatus());
  }
}

/**
 * Convert a gateway WebSocket URL (`wss://host[/path]`) into the HTTPS base
 * the sync API lives on. Returns null for anything unparsable.
 */
export function syncBaseUrlFromAgentUrl(
  agentUrl: string | null,
): string | null {
  if (agentUrl == null || agentUrl.length === 0) return null;
  try {
    const url = new URL(agentUrl);
    const protocol =
      url.protocol === 'wss:' || url.protocol === 'https:'
        ? 'https:'
        : url.protocol === 'ws:' || url.protocol === 'http:'
          ? 'http:'
          : null;
    if (protocol == null) return null;
    return `${protocol}//${url.host}`;
  } catch {
    return null;
  }
}
